import React, { useState, useEffect } from 'react';
import { Box, Collapse, IconButton, Typography, LinearProgress } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { ChecklistTierSummary } from '../../services/onboardingService';
import { ChecklistItem } from './ChecklistItem';

interface TierSectionProps {
  tier: ChecklistTierSummary;
  defaultExpanded?: boolean;
}

export const TierSection: React.FC<TierSectionProps> = ({ tier, defaultExpanded }) => {
  const allComplete = tier.completedCount === tier.totalCount && tier.totalCount > 0;
  const [expanded, setExpanded] = useState(defaultExpanded ?? !allComplete);

  // Auto-collapse when all items in a tier finish — keeps the drawer tidy.
  useEffect(() => {
    if (allComplete) setExpanded(false);
  }, [allComplete]);

  const nextIncompleteId = tier.items.find((i) => !i.completed)?.id;
  const progressPercent =
    tier.totalCount === 0 ? 0 : (tier.completedCount / tier.totalCount) * 100;

  return (
    <Box
      sx={{
        borderRadius: 2,
        bgcolor: '#FFFFFF',
        border: '1px solid #EDE3D3',
        overflow: 'hidden',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: '#FAF5EC' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#3B2E1A', fontSize: '0.875rem' }}
            >
              {tier.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: allComplete ? '#2F7D32' : '#8B6B4A',
                fontWeight: 600,
                fontSize: '0.75rem',
                ml: 1,
              }}
            >
              {tier.completedCount} of {tier.totalCount}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: '#EFE5D3',
              '& .MuiLinearProgress-bar': {
                bgcolor: allComplete ? '#2F7D32' : '#8B4513',
                borderRadius: 2,
              },
            }}
          />
        </Box>
        <IconButton size="small" sx={{ color: '#8B6B4A' }} aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1, pt: 0 }}>
          {tier.items.map((item) => (
            <ChecklistItem key={item.id} item={item} active={item.id === nextIncompleteId} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
